import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Users, Building2, Activity, Crown, AlertTriangle, MessageCircle, ShieldCheck, Database, FileText, FilePieChart, FileSignature, Download, Inbox, Mail, ExternalLink, Search, Trash2, KeyRound, Monitor, Smartphone } from "lucide-react";

const Stat = ({ icon: Icon, label, value, tone }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={`stat-${label.replace(/\s/g, "-").toLowerCase()}`}>
    <div className={`w-10 h-10 rounded-xl ${tone} flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
    <div className="font-display text-3xl font-bold text-slate-900">{value ?? "—"}</div>
    <div className="text-sm text-slate-500 mt-1">{label}</div>
  </div>
);

const CollateralCard = ({ testId, icon: Icon, title, desc, onDownload }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-start gap-4" data-testid={testId}>
    <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-800 flex items-center justify-center shrink-0">
      <Icon className="w-5 h-5" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-display font-bold text-slate-900">{title}</div>
      <div className="text-sm text-slate-600 mt-1">{desc}</div>
    </div>
    <button
      data-testid={`${testId}-download`}
      onClick={onDownload}
      className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-4 py-2 text-sm font-semibold flex items-center gap-2 shrink-0"
    >
      <Download className="w-4 h-4" /> Download
    </button>
  </div>
);

export default function AdminPanel() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [trusts, setTrusts] = useState([]);
  const [leads, setLeads] = useState({ items: [], counts: {}, statuses: ["new", "contacted", "qualified", "lost"] });
  const [leadFilter, setLeadFilter] = useState("all");
  const [tab, setTab] = useState("overview");
  const [userQuery, setUserQuery] = useState("");
  const [previewDevice, setPreviewDevice] = useState("mobile");

  const loadUsers = async (q = "") => {
    try {
      const { data } = await api.get(`/admin/users?limit=100${q ? `&q=${encodeURIComponent(q)}` : ""}`);
      setUsers(data);
    } catch { /* admin-only */ }
  };

  const loadLeads = async (status = leadFilter) => {
    try {
      const { data } = await api.get(`/admin/leads${status && status !== "all" ? `?status=${status}` : ""}`);
      setLeads(data);
    } catch { /* admin-only */ }
  };

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => toast.error("Admin access required"));
    api.get("/admin/users?limit=50").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/trusts").then((r) => setTrusts(r.data)).catch(() => {});
    loadLeads("all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grant = async (uid) => {
    try { await api.post(`/admin/users/${uid}/premium?days=30`); toast.success("Granted 30 days Premium"); await loadUsers(userQuery); }
    catch { toast.error("Failed"); }
  };

  const revoke = async (uid) => {
    try { await api.post(`/admin/users/${uid}/premium/revoke`); toast.success("Premium revoked"); await loadUsers(userQuery); }
    catch { toast.error("Failed"); }
  };

  const sendReset = async (uid, email) => {
    try {
      const { data } = await api.post(`/admin/users/${uid}/send-reset`);
      toast.success(data.emailed ? `Reset code emailed to ${email}` : "Code created (email not configured)");
    } catch { toast.error("Could not send reset"); }
  };

  const removeUser = async (uid, email) => {
    if (!window.confirm(`Permanently delete ${email} and ALL their data (safety plan, mood, vault)? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${uid}`); toast.success(`Deleted ${email}`); await loadUsers(userQuery); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const searchUsers = (e) => { e.preventDefault(); loadUsers(userQuery); };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
      toast.success(`Marked ${status}`);
      await loadLeads(leadFilter);
    } catch { toast.error("Could not update lead"); }
  };

  const downloadLeadsCsv = async () => {
    try {
      const token = localStorage.getItem("sh_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/leads/export.csv`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `safehaven-nhs-leads.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads CSV downloaded");
    } catch { toast.error("Could not export CSV"); }
  };

  const filterLeads = (s) => { setLeadFilter(s); loadLeads(s); };

  const statusTone = {
    new: "bg-amber-50 text-amber-800 border-amber-200",
    contacted: "bg-blue-50 text-blue-800 border-blue-200",
    qualified: "bg-emerald-50 text-emerald-800 border-emerald-200",
    lost: "bg-slate-100 text-slate-600 border-slate-200",
  };

  const downloadCollateral = async (path, filename) => {
    try {
      toast.loading("Generating PDF…", { id: path });
      const token = localStorage.getItem("sh_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales/${path}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let detail = res.statusText;
        try { const j = await res.json(); if (j?.detail) detail = j.detail; } catch { /* not JSON */ }
        throw new Error(detail);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded", { id: path });
    } catch (e) {
      toast.error(e.message || "Could not generate PDF", { id: path });
    }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="admin-panel">
      <header>
        <div className="flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-teal-700" /><h1 className="font-display text-3xl font-bold text-slate-900">Admin Panel</h1></div>
        <p className="text-slate-600 mt-2">Site-wide oversight for {user?.email}.</p>
      </header>

      <div className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {["overview", "users", "trusts", "leads", "collateral", "preview"].map((k) => {
          const badge = k === "leads" && (leads.counts.new || 0) > 0 ? leads.counts.new : null;
          return (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 whitespace-nowrap inline-flex items-center gap-2 ${tab === k ? "border-teal-700 text-teal-800" : "border-transparent text-slate-500"}`}>
              {k[0].toUpperCase() + k.slice(1)}
              {badge && <span data-testid="leads-new-badge" className="bg-amber-100 text-amber-800 text-[10px] font-bold rounded-full px-1.5 py-0.5">{badge}</span>}
            </button>
          );
        })}
      </div>

      {tab === "overview" && overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Users} label="Users total" value={overview.users_total} tone="bg-teal-50 text-teal-800" />
          <Stat icon={Crown} label="Premium users" value={overview.users_premium} tone="bg-amber-50 text-amber-800" />
          <Stat icon={Database} label="Research consented" value={overview.users_research_consented} tone="bg-emerald-50 text-emerald-800" />
          <Stat icon={Building2} label="Trusts" value={overview.trusts_total} tone="bg-slate-100 text-slate-800" />
          <Stat icon={Activity} label="Check-ins (7d)" value={overview.checkins_7d} tone="bg-blue-50 text-blue-800" />
          <Stat icon={Activity} label="Mood entries (7d)" value={overview.mood_entries_7d} tone="bg-purple-50 text-purple-800" />
          <Stat icon={MessageCircle} label="AI messages (7d)" value={overview.chat_msgs_7d} tone="bg-rose-50 text-rose-800" />
          <Stat icon={AlertTriangle} label="Deterioration nudges (7d)" value={overview.deterioration_nudges_7d} tone="bg-red-50 text-red-800" />
        </div>
      )}

      {tab === "users" && (
        <div className="space-y-3">
          <form onSubmit={searchUsers} className="flex gap-2" data-testid="admin-user-search-form">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="admin-user-search" value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder="Search by name or email…" className="w-full border border-slate-300 rounded-xl pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <button type="submit" className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl px-4 py-2 text-sm font-semibold">Search</button>
            {userQuery && <button type="button" onClick={() => { setUserQuery(""); loadUsers(""); }} className="text-slate-500 text-sm px-2">Clear</button>}
          </form>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="admin-users-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr><th className="p-3">Name</th><th>Email</th><th>Mode</th><th>Role</th><th>Premium</th><th className="text-right pr-3">Actions</th></tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-slate-400">No users found.</td></tr>
              )}
              {users.map((u) => {
                const isPremium = u.premium_until && new Date(u.premium_until) > new Date();
                return (
                <tr key={u.id} className="border-t border-slate-100 align-middle">
                  <td className="p-3 font-medium text-slate-900">{u.name}</td>
                  <td className="text-slate-700">{u.email}</td>
                  <td className="text-slate-500">{u.mode}</td>
                  <td className="text-slate-500">{u.role || "user"}</td>
                  <td className="text-slate-500">{isPremium ? new Date(u.premium_until).toLocaleDateString("en-GB") : "—"}</td>
                  <td className="pr-3">
                    <div className="flex items-center justify-end gap-3">
                      {isPremium ? (
                        <button data-testid={`revoke-premium-${u.id}`} onClick={() => revoke(u.id)} className="text-amber-700 text-xs font-semibold hover:underline inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" />Revoke</button>
                      ) : (
                        <button data-testid={`grant-premium-${u.id}`} onClick={() => grant(u.id)} className="text-teal-700 text-xs font-semibold hover:underline inline-flex items-center gap-1"><Crown className="w-3.5 h-3.5" />+30d</button>
                      )}
                      <button data-testid={`send-reset-${u.id}`} onClick={() => sendReset(u.id, u.email)} className="text-blue-700 text-xs font-semibold hover:underline inline-flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" />Reset</button>
                      <button data-testid={`delete-user-${u.id}`} onClick={() => removeUser(u.id, u.email)} className="text-rose-600 text-xs font-semibold hover:underline inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" />Delete</button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "preview" && (
        <div className="space-y-4" data-testid="admin-preview">
          <div className="flex items-center gap-2">
            <button data-testid="preview-toggle-mobile" onClick={() => setPreviewDevice("mobile")} className={`px-4 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-2 ${previewDevice === "mobile" ? "bg-teal-700 text-white" : "bg-white border border-slate-300 text-slate-600"}`}><Smartphone className="w-4 h-4" />Mobile view</button>
            <button data-testid="preview-toggle-desktop" onClick={() => setPreviewDevice("desktop")} className={`px-4 py-2 rounded-xl text-sm font-semibold inline-flex items-center gap-2 ${previewDevice === "desktop" ? "bg-teal-700 text-white" : "bg-white border border-slate-300 text-slate-600"}`}><Monitor className="w-4 h-4" />Web view</button>
          </div>
          <p className="text-sm text-slate-500">Live preview of the app. Mobile view mimics a phone screen; web view uses the full desktop layout.</p>
          <div className="flex justify-center bg-slate-100 rounded-2xl p-6 border border-slate-200">
            <div
              data-testid="preview-frame"
              className={`bg-white overflow-hidden transition-all duration-300 ${previewDevice === "mobile" ? "rounded-[2.2rem] border-[10px] border-slate-900 shadow-2xl" : "rounded-xl border border-slate-300 shadow-lg w-full"}`}
              style={previewDevice === "mobile" ? { width: 390, height: 720 } : { height: 720 }}
            >
              <iframe title="app-preview" src="/" className="w-full h-full" style={{ border: 0 }} />
            </div>
          </div>
        </div>
      )}

      {tab === "trusts" && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="admin-trusts-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr><th className="p-3">Trust</th><th>Slug</th><th>Members</th><th>DPA Signed</th><th>Invite code</th><th>Created</th></tr>
            </thead>
            <tbody>
              {trusts.map((t) => (
                <tr key={t.slug} className="border-t border-slate-100">
                  <td className="p-3 font-medium text-slate-900">{t.name}</td>
                  <td className="text-slate-500 font-mono text-xs">{t.slug}</td>
                  <td className="text-slate-700">{t.members}</td>
                  <td>{t.dpa_signed_at ? <span className="text-emerald-700 font-semibold">✓ {new Date(t.dpa_signed_at).toLocaleDateString("en-GB")}</span> : <span className="text-amber-700">Pending</span>}</td>
                  <td className="font-mono text-teal-800">{t.invite_code}</td>
                  <td className="text-slate-400 text-xs">{new Date(t.created_at).toLocaleDateString("en-GB")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "leads" && (
        <div className="space-y-3" data-testid="admin-leads">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-1 bg-slate-100 rounded-full p-1">
              {["all", ...leads.statuses].map((s) => (
                <button
                  key={s}
                  data-testid={`leads-filter-${s}`}
                  onClick={() => filterLeads(s)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full ${leadFilter === s ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
                >
                  {s[0].toUpperCase() + s.slice(1)}
                  <span className="ml-1.5 text-slate-400">{leads.counts[s] ?? 0}</span>
                </button>
              ))}
            </div>
            <button data-testid="leads-export-csv" onClick={downloadLeadsCsv} className="bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-xl px-4 py-2 inline-flex items-center gap-2">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>

          {leads.items.length === 0 ? (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center" data-testid="leads-empty">
              <Inbox className="w-10 h-10 text-slate-300 mx-auto" />
              <div className="font-display font-bold text-slate-900 mt-3">No leads in this view yet</div>
              <div className="text-sm text-slate-500 mt-1">Share <span className="font-mono">/for-nhs</span> on LinkedIn or in cold emails — submissions land here.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {leads.items.map((l) => (
                <div key={l.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={`lead-card-${l.id}`}>
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display font-bold text-slate-900">{l.name}</div>
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${statusTone[l.status] || statusTone.new}`}>{l.status || "new"}</span>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5">{l.role} · <span className="text-slate-900 font-medium">{l.organisation}</span></div>
                      <a href={`mailto:${l.email}?subject=SafeHaven%20%E2%80%94%20${encodeURIComponent(l.organisation)}`} className="text-sm text-teal-700 hover:underline mt-1 inline-flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" /> {l.email} <ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                      {l.message && (
                        <div className="mt-3 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-xl p-3 whitespace-pre-line">{l.message}</div>
                      )}
                      <div className="text-xs text-slate-400 mt-3">
                        {new Date(l.created_at).toLocaleString("en-GB")}
                        {l.referer && <> · referred from <span className="font-mono">{l.referer}</span></>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {leads.statuses.map((s) => (
                        s !== (l.status || "new") && (
                          <button
                            key={s}
                            data-testid={`lead-${l.id}-mark-${s}`}
                            onClick={() => updateLeadStatus(l.id, s)}
                            className="text-xs font-semibold border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white rounded-lg px-3 py-1.5"
                          >
                            Mark {s}
                          </button>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "collateral" && (
        <div className="space-y-3" data-testid="admin-collateral">
          <p className="text-sm text-slate-600 max-w-2xl">
            Procurement collateral for outreach to NHS Trusts, commissioners and CQC inspectors. All three documents are generated server-side and contain no real patient data — the sample Impact Report uses deterministic seeded values labelled <em>illustrative</em>.
          </p>
          <CollateralCard
            testId="collateral-pitch"
            icon={FileText}
            title="One-page pitch sheet"
            desc="Branded A4 brief — problem · approach · feature grid · NHS readiness · pricing tiers. Designed to attach to a cold outreach email."
            onDownload={() => downloadCollateral("pitch-sheet.pdf", "safehaven-pitch-sheet.pdf")}
          />
          <CollateralCard
            testId="collateral-sample-ir"
            icon={FilePieChart}
            title="Sample Impact Report"
            desc="A populated example of the quarterly Impact Report for a fictional “Demo NHS Foundation Trust”, with a realistic 12-week trend, headline figures and top warning-sign themes. Use this to show a commissioner what they'd actually receive."
            onDownload={() => downloadCollateral("sample-impact-report.pdf", "safehaven-sample-impact-report.pdf")}
          />
          <CollateralCard
            testId="collateral-dpa"
            icon={FileSignature}
            title="DPA / DPIA procurement summary"
            desc="Standalone PDF covering the in-product Data Processing Agreement and the Data Protection Impact Assessment. Attach to procurement / IG reviews."
            onDownload={() => downloadCollateral("dpa-summary.pdf", "safehaven-dpa-summary.pdf")}
          />
        </div>
      )}
    </div>
  );
}
